import { apiClient } from './apiClient'

/**
 * Admin API service client.
 * Provides typed methods for all admin portal endpoints.
 * All requests go through the shared apiClient which handles auth tokens automatically.
 */

const ADMIN_BASE = '/admin'

export const adminService = {
  // ─── Dashboard ───────────────────────────────────────────────────────────────
  getDashboardMetrics: () =>
    apiClient.get(`${ADMIN_BASE}/dashboard/metrics`),

  getDashboardTrends: () =>
    apiClient.get(`${ADMIN_BASE}/dashboard/trends`),

  // ─── Users ───────────────────────────────────────────────────────────────────
  listUsers: (params?: Record<string, any>) =>
    apiClient.get(`${ADMIN_BASE}/users`, { params }),

  createUser: (data: Record<string, any>) =>
    apiClient.post(`${ADMIN_BASE}/users`, data),

  updateUser: (id: string, data: Record<string, any>) =>
    apiClient.put(`${ADMIN_BASE}/users/${id}`, data),

  disableUser: (id: string) =>
    apiClient.patch(`${ADMIN_BASE}/users/${id}/disable`),

  enableUser: (id: string) =>
    apiClient.patch(`${ADMIN_BASE}/users/${id}/enable`),

  deleteUser: (id: string) =>
    apiClient.delete(`${ADMIN_BASE}/users/${id}`),

  resetPassword: (id: string) =>
    apiClient.post(`${ADMIN_BASE}/users/${id}/reset-password`),

  verifyEmail: (id: string) =>
    apiClient.post(`${ADMIN_BASE}/users/${id}/verify-email`),

  linkBusiness: (userId: string, businessId: string) =>
    apiClient.post(`${ADMIN_BASE}/users/${userId}/link-business`, { businessId }),

  unlinkBusiness: (userId: string) =>
    apiClient.post(`${ADMIN_BASE}/users/${userId}/unlink-business`),

  // ─── Businesses ──────────────────────────────────────────────────────────────
  createBusiness: (data: Record<string, any>) =>
    apiClient.post(`${ADMIN_BASE}/businesses`, data),

  listBusinesses: (params?: Record<string, any>) =>
    apiClient.get(`${ADMIN_BASE}/businesses`, { params }),

  getBusinessDetail: (id: string) =>
    apiClient.get(`${ADMIN_BASE}/businesses/${id}`),

  updateBusinessField: (id: string, data: Record<string, any>) =>
    apiClient.put(`${ADMIN_BASE}/businesses/${id}/fields`, data),

  triggerRecalculation: (id: string) =>
    apiClient.post(`${ADMIN_BASE}/businesses/${id}/recalculate`),

  triggerBulkRecalculation: (businessIds: string[]) =>
    apiClient.post(`${ADMIN_BASE}/businesses/recalculate-bulk`, { businessIds }),

  // ─── Impersonation ───────────────────────────────────────────────────────────
  startImpersonation: (targetUserId: string) =>
    apiClient.post(`${ADMIN_BASE}/impersonation/start`, { targetUserId }),

  endImpersonation: () =>
    apiClient.post(`${ADMIN_BASE}/impersonation/end`),

  getImpersonationSession: () =>
    apiClient.get(`${ADMIN_BASE}/impersonation/session`),

  // ─── System ──────────────────────────────────────────────────────────────────
  getSettings: () =>
    apiClient.get(`${ADMIN_BASE}/system/settings`),

  updateSetting: (category: string, key: string, value: any) =>
    apiClient.put(`${ADMIN_BASE}/system/settings/${category}/${key}`, { value }),

  getCronJobs: () =>
    apiClient.get(`${ADMIN_BASE}/system/cron-jobs`),

  triggerCronJob: (id: string) =>
    apiClient.post(`${ADMIN_BASE}/system/cron-jobs/${id}/trigger`),

  // ─── Lenders ─────────────────────────────────────────────────────────────────
  listLenders: (params?: Record<string, any>) =>
    apiClient.get(`${ADMIN_BASE}/lenders`, { params }),

  createLender: (data: Record<string, any>) =>
    apiClient.post(`${ADMIN_BASE}/lenders`, data),

  getLenderAnalytics: (params?: Record<string, any>) =>
    apiClient.get(`${ADMIN_BASE}/lenders/analytics`, { params }),

  // ─── Access Control ──────────────────────────────────────────────────────────
  getPermissionMatrix: () =>
    apiClient.get(`${ADMIN_BASE}/access-control`),

  grantPermission: (role: string, feature: string) =>
    apiClient.post(`${ADMIN_BASE}/access-control/grant`, { role, feature }),

  denyPermission: (role: string, feature: string) =>
    apiClient.post(`${ADMIN_BASE}/access-control/deny`, { role, feature }),

  setUserOverride: (userId: string, feature: string, granted: boolean) =>
    apiClient.post(`${ADMIN_BASE}/access-control/user-override`, { userId, feature, granted }),

  removeUserOverride: (userId: string, feature: string) =>
    apiClient.delete(`${ADMIN_BASE}/access-control/user-override`, { data: { userId, feature } }),

  listFeatureFlags: () =>
    apiClient.get(`${ADMIN_BASE}/access-control/feature-flags`),

  createFeatureFlag: (data: { name: string; description?: string; enabled: boolean; targetRoles: string[]; conditions?: Record<string, unknown> }) =>
    apiClient.post(`${ADMIN_BASE}/access-control/feature-flags`, data),

  toggleFeatureFlag: (id: string, enabled: boolean) =>
    apiClient.put(`${ADMIN_BASE}/access-control/feature-flags/${id}/toggle`, { enabled }),

  // ─── Audit Log ───────────────────────────────────────────────────────────────
  getAuditLog: (params?: Record<string, any>) =>
    apiClient.get(`${ADMIN_BASE}/audit-log`, { params }),

  exportAuditLog: (params?: Record<string, any>) =>
    apiClient.post(`${ADMIN_BASE}/audit-log/export`, params),

  // ─── Subscriptions ───────────────────────────────────────────────────────────
  listPlans: (params?: Record<string, any>) =>
    apiClient.get(`${ADMIN_BASE}/subscriptions/plans`, { params }),

  createPlan: (data: Record<string, any>) =>
    apiClient.post(`${ADMIN_BASE}/subscriptions/plans`, data),

  updatePlan: (id: string, data: Record<string, any>) =>
    apiClient.put(`${ADMIN_BASE}/subscriptions/plans/${id}`, data),

  assignPlan: (userId: string, planId: string) =>
    apiClient.post(`${ADMIN_BASE}/subscriptions/assign`, { userId, planId }),

  getSubscriptionAnalytics: () =>
    apiClient.get(`${ADMIN_BASE}/subscriptions/analytics`),

  // ─── API Keys ────────────────────────────────────────────────────────────────
  listAPIKeys: () =>
    apiClient.get(`${ADMIN_BASE}/api-keys`),

  createAPIKey: (data: { name: string; permissionScope: string[] }) =>
    apiClient.post(`${ADMIN_BASE}/api-keys`, data),

  revokeAPIKey: (id: string) =>
    apiClient.delete(`${ADMIN_BASE}/api-keys/${id}`),

  // ─── Health ──────────────────────────────────────────────────────────────────
  getHealthMetrics: () =>
    apiClient.get(`${ADMIN_BASE}/health/metrics`),

  getErrorRates: () =>
    apiClient.get(`${ADMIN_BASE}/health/errors`),

  getServiceStatus: () =>
    apiClient.get(`${ADMIN_BASE}/health/services`),

  // ─── Export ──────────────────────────────────────────────────────────────────
  exportBusinessData: (params: Record<string, any>) =>
    apiClient.post(`${ADMIN_BASE}/export/businesses`, params),

  exportUserData: (params: Record<string, any>) =>
    apiClient.post(`${ADMIN_BASE}/export/users`, params),

  getExportStatus: (exportId: string) =>
    apiClient.get(`${ADMIN_BASE}/export/${exportId}/status`),
}
